import {useState} from 'react';

const SkillBubble = ({ skill, onRemove=null }) => {
    const styles = {
        bubble: {
            display: 'inline-block',
            backgroundColor: '#6B3A8F', // Purple color
            color: 'white',
            padding: '0.25rem 0.75rem',
            borderRadius: '9999px',
            fontSize: '0.875rem',
            fontWeight: '500',
            marginRight: '0.5rem',
            marginBottom: '0.5rem',
            transition: 'transform 0.3s, opacity 0.3s',
        },
        missingBubble: {
            border: '1.055px solid #A0A0C0', // Slightly desaturated border
            background: 'rgba(173, 160, 202, 0.30)', // Soft lavender
            color: 'white',
            opacity: 0.7,
        },
        removeButton: {
            marginLeft: '6px',
            background: 'none',
            border: 'none',
            color: '#FFF',
            cursor: 'pointer',
            fontSize: '12px',
        }
    };

    const handleRemove = (e) => {
        e.preventDefault();
        onRemove(skill);
    };

    return (
        <span 
            style={{
                ...styles.bubble,
                ...(skill.isMissing ? styles.missingBubble : {})
            }}
            data-cy={`resumeFormSkillShow${skill.name}-span`}
        >
            {skill.name}
            {onRemove && (
                <button 
                    onClick={handleRemove} 
                    style={styles.removeButton}
                    type="button"
                    data-cy={`resumeFormSkillRemove${skill.name}-button`}
                >
                    ×
                </button>
            )}
        </span>
    );
};

export const SkillBubbles = ({ skills, isMissing = true }) => {
    // Only render if there are missing keywords
    if (!skills || skills.length === 0) {
        return null;
    }

    // Transform keywords into objects with a flag
    const formattedSkills = skills.map(keyword => ({
        name: keyword,
        isMissing: isMissing
    }));

    return (
        <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'start',
            paddingTop: '2%'
        }}>
            {formattedSkills.map((skill) => (
                <SkillBubble 
                    key={skill.name}
                    skill={skill}
                />
            ))}
        </div>
    );
};

export const SkillsInput = ({ onChange, initialSkills = [] }) => {
    const [skills, setSkills] = useState(initialSkills);
    const [currentSkill, setCurrentSkill] = useState('');

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && currentSkill.trim()) {
            if (!skills.includes(currentSkill.trim())) {
                const newSkills = [...skills, currentSkill.trim()];
                setSkills(newSkills);
                setCurrentSkill('');
                onChange && onChange(newSkills);
            }
            e.preventDefault();
        }
    };

    const removeSkill = (skillToRemove) => {
        const newSkills = skills.filter(skill => 
            typeof skillToRemove === 'string' 
                ? skill !== skillToRemove 
                : skill !== skillToRemove.name
        );
        setSkills(newSkills);
        onChange && onChange(newSkills);
    };

    const styles = {
        container: {
            fontFamily: 'Poppins',
        },
        input: {
            width: '100%',
            padding: '8px',
            borderRadius: '8px',
            border: '1px solid #C2B2C9',
            background: 'rgba(26, 26, 26, 0.5)',
            color: '#FFF',
            marginBottom: '10px',
        },
        bubbleContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
        }
    };

    return (
        <div style={styles.container}>
            <input 
                type="text"
                value={currentSkill}
                onChange={(e) => setCurrentSkill(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter a skill and press Enter"
                style={styles.input}
                data-cy="resumeFormSkill-input"
            />
            <div style={styles.bubbleContainer}>
                {skills.map(skill => (
                    <SkillBubble 
                        skill={{ name: skill, isMissing: false }} 
                        onRemove={removeSkill}
                    />
                ))}
            </div>
        </div>
    );
};

export default SkillsInput;